import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PutUserAlbumVideoLikesResult,
  PutUserAlbumVideoLikesOptions,
  DeleteUserAlbumVideoLikesResult,
  DeleteUserAlbumVideoLikesOptions,
  PutUserAlbumVideoLikesFields,
  DeleteUserAlbumVideoLikesFields,
  putUserAlbumVideoLikes,
  deleteUserAlbumVideoLikes,
} from '../fetchers/user/album/video/likes';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UsePutUserAlbumVideoLikesOptions<Fields extends PutUserAlbumVideoLikesFields> =
  PutUserAlbumVideoLikesOptions<Fields>;

/**
 * Cause the user to like a video
 * This method causes the authenticated user to like the specified video. The user can't like their own video.
 */

export function usePutUserAlbumVideoLikes<Fields extends PutUserAlbumVideoLikesFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PutUserAlbumVideoLikesResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePutUserAlbumVideoLikesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/users/${options.where.userId}/albums/${options.where.albumId}/videos/${
            options.where.videoId
          }/likes${serializeQuery(options)}`,
          putUserAlbumVideoLikes({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePutUserAlbumVideoLikes, {
    endpoint: '/users/:userId/albums/:albumId/videos/:videoId/likes',
    method: 'PUT',
  });
}

type UseDeleteUserAlbumVideoLikesOptions<Fields extends DeleteUserAlbumVideoLikesFields> =
  DeleteUserAlbumVideoLikesOptions<Fields>;

/**
 * Cause the user to unlike a video
 * This method causes the authenticated user to unlike the specified video.
 */

export function useDeleteUserAlbumVideoLikes<Fields extends DeleteUserAlbumVideoLikesFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteUserAlbumVideoLikesResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteUserAlbumVideoLikesOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/users/${options.where.userId}/albums/${options.where.albumId}/videos/${
            options.where.videoId
          }/likes${serializeQuery(options)}`,
          deleteUserAlbumVideoLikes({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteUserAlbumVideoLikes, {
    endpoint: '/users/:userId/albums/:albumId/videos/:videoId/likes',
    method: 'DELETE',
  });
}
